'use client'

import { useEffect, useState } from 'react'
import DateRangePicker from '~/components/booking/mobile/search/arrival-dept-date'
import type { DateRange } from 'react-day-picker'
import BreadcrumbMobile from '~/components/ui/breadcrumb-mobile'
import Card from '~/components/ui/card'
import HotelSelector from '~/components/booking/shared/hotel-selector'
import GuestSelector from '~/components/booking/shared/guest-selector'
import CardItem from './card-item'
import { IconCalendarSvg } from '~/icons/ic_calendar'
import { IconUserSvg } from '~/icons/ic_user'
import { IconSearchSvg } from '~/icons/ic_search'
import { useRouter } from 'next/navigation'
import type { Hotel } from '~/hooks/type'
import { formatDateRangeShort, formatDateRangeFull, formatDateToISO } from '~/lib/date-utils'
import { useLocale } from '~/contexts/LocaleContext'
import { getTranslation } from '~/lib/i18n-utils'
import Header from '~/components/sections/header'
import GuestCard from './guest-card'
import { useBookingCart } from '~/stores/booking-cart'
import { useBookingFlow } from '~/stores/booking-flow'
import { useDynamicTitle } from '~/hooks/use-dynamic-title'

type Section = 'hotel' | 'dates' | 'guests' | null

interface BookingSearchProps {
  hotels: Hotel[]
  onSubmit: (data: {
    hotelId: string
    checkInDate: string
    checkOutDate: string
    adults: number
    kidsOver6: number
    kidsUnder6: number
    roomType: string
  }) => void
  initialValues?: {
    hotelId: string
    checkInDate: string
    checkOutDate: string
    adults: number
    kidsOver6: number
    kidsUnder6: number
    roomType: string
  }
  onBackToSearch: () => void
}

export default function BookingSearch({
  hotels = [],
  onSubmit,
  initialValues,
  onBackToSearch,
}: BookingSearchProps) {
  const { locale } = useLocale()
  const t = getTranslation(locale)
  const today = new Date()
  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)
  const [activeSection, setActiveSection] = useState<Section>('hotel')
  const [adults, setAdults] = useState(initialValues?.adults || 1)
  const [kidsOver6, setKidsOver6] = useState(initialValues?.kidsOver6 || 0)
  const [kidsUnder6, setKidsUnder6] = useState(initialValues?.kidsUnder6 || 0)
  const [selectedHotel, setSelectedHotel] = useState(initialValues?.hotelId || '')
  const [dateRange, setDateRange] = useState<DateRange | undefined>(
    initialValues && initialValues.checkInDate && initialValues.checkOutDate
      ? {
          from: new Date(initialValues.checkInDate),
          to: new Date(initialValues.checkOutDate),
        }
      : {
          from: today,
          to: tomorrow,
        }
  )
  const { clearCart } = useBookingCart()
  const { clearBookingData } = useBookingFlow()

  // Reset all search parameters to default values
  const handleReset = () => {
    setSelectedHotel('')
    setDateRange({
      from: today,
      to: tomorrow,
    })
    setAdults(1)
    setKidsOver6(0)
    setKidsUnder6(0)
    setActiveSection('hotel')
  }

  useDynamicTitle(selectedHotel)

  // Auto-open dates section after selecting hotel
  const handleHotelSelect = (hotel: string) => {
    setSelectedHotel(hotel)
    setTimeout(() => {
      setActiveSection('dates')
    }, 300)
    setTimeout(() => {
      setActiveSection('dates')
    }, 300)
  }

  // Auto-open guests section after selecting dates
  const handleDateSelect = (range: DateRange | undefined) => {
    setDateRange(range)
    if (range?.from && range?.to) {
      setTimeout(() => {
        setActiveSection('guests')
      }, 300)
      setTimeout(() => {
        setActiveSection('guests')
      }, 300)
    }
  }

  // Close collapsible after selecting guests
  const handleGuestApply = () => {
    setTimeout(() => {
      setActiveSection(null)
    }, 300)
    setTimeout(() => {
      setActiveSection(null)
    }, 300)
  }

  // Use hotels from props, or fallback to sample data if empty
  const hotelOptions =
    hotels.length > 0
      ? hotels
          .filter((hotel) => hotel.hotelId)
          .map((hotel) => ({
            label: hotel.fullName || '',
            value: hotel.hotelId || '',
          }))
      : []

  const formatGuestCount = () => {
    return `${adults} ${t('booking.adults')}, ${kidsOver6 + kidsUnder6} ${t('booking.children')}`
  }

  // Xử lý click vào thông tin ngày
  const handleDateClick = () => {
    setActiveSection('dates')
  }

  // Xử lý click vào thông tin khách
  const handleGuestClick = () => {
    setActiveSection('guests')
  }

  const handleSearch = () => {
    clearBookingData()
    clearCart()
    if (selectedHotel && dateRange?.from && dateRange?.to) {
      onSubmit({
        hotelId: selectedHotel,
        checkInDate: formatDateToISO(dateRange.from),
        checkOutDate: formatDateToISO(dateRange.to),
        adults,
        kidsOver6,
        kidsUnder6,
        roomType: '',
      })
    }
  }

  return (
    <>
      <Header />
      <BreadcrumbMobile
        title={t('booking.roomList')}
        dateRange={formatDateRangeShort(dateRange, locale, t)}
        guestInfo={formatGuestCount()}
        onDateClick={handleDateClick}
        onGuestClick={handleGuestClick}
        onBackToSearch={onBackToSearch}
      />

      <div className="h-full px-4 py-6 pb-20">
        <div className="mx-auto max-w-md">
          {/* Search Input */}
          {activeSection !== 'hotel' && (
            <CardItem
              title={t('booking.hotel')}
              description={hotelOptions.find((h) => h.value === selectedHotel)?.label || ''}
              onClick={() => setActiveSection('hotel')}
              icon={<IconSearchSvg />}
              placeholder={t('booking.whereTo')}
            />
          )}

          {activeSection === 'hotel' && (
            <HotelSelector
              hotels={hotelOptions}
              selectedHotel={selectedHotel}
              onHotelSelect={handleHotelSelect}
            />
          )}

          {/* Date Section */}
          {activeSection !== 'dates' && (
            <CardItem
              title={t('booking.checkInOut')}
              description={formatDateRangeFull(dateRange, locale, t)}
              onClick={() => setActiveSection('dates')}
              icon={<IconCalendarSvg />}
            />
          )}

          {activeSection === 'dates' && (
            <Card className="mb-4" padding={0}>
              <DateRangePicker
                selectedDates={dateRange}
                onSelect={handleDateSelect}
                onClose={() => setActiveSection('guests')}
                hotelId={selectedHotel}
              />
            </Card>
          )}

          {/* Guest Section */}
          {activeSection !== 'guests' && (
            <CardItem
              title={t('booking.guestCount')}
              description={formatGuestCount()}
              onClick={() => setActiveSection('guests')}
              icon={<IconUserSvg />}
            />
          )}
          {activeSection === 'guests' && (
            <Card className="mb-4" padding={0}>
              <GuestCard
                adults={adults}
                minAdults={1}
                kidsOver6={kidsOver6}
                minKidsOver6={0}
                kidsUnder6={kidsUnder6}
                minKidsUnder6={0}
                setAdults={setAdults}
                setKidsOver6={setKidsOver6}
                setKidsUnder6={setKidsUnder6}
                onApply={handleGuestApply}
              />
            </Card>
          )}
        </div>
      </div>

      {/* Fixed bottom buttons */}
      <div className="fixed inset-x-0 bottom-0 bg-white px-4 py-4 shadow-md">
        <div className="mx-auto flex max-w-md justify-between gap-2">
          <button
            className="w-1/2 rounded-md bg-[#EBE3D6] py-3 text-sm font-semibold text-[#855f42]"
            onClick={handleReset}
          >
            {t('booking.reset')}
          </button>
          <button
            className={`w-1/2 rounded-md py-2 text-sm font-semibold ${
              !selectedHotel
                ? 'cursor-not-allowed bg-[#c2b0a2] text-gray-200'
                : 'bg-primary-500 text-white'
            }`}
            onClick={handleSearch}
            disabled={!selectedHotel}
          >
            {t('booking.search')}
          </button>
        </div>
      </div>
    </>
  )
}
